import { PLAYER_COLORS } from "@flood-watch/shared";

export class GameUI {
  constructor() {
    this.panels = {
      menu: document.querySelector("#menu"),
      lobby: document.querySelector("#lobby"),
      results: document.querySelector("#results"),
    };
    this.hud = document.querySelector("#hud");
    this.touchControls = document.querySelector("#touch-controls");
    this.toastElement = document.querySelector("#toast");
    this.toastTimer = null;
    this.lastCountdown = null;
  }

  showPanel(name) {
    Object.entries(this.panels).forEach(([key, element]) => element.classList.toggle("visible", key === name));
    if (name) {
      this.hud.classList.add("hidden");
      this.touchControls.classList.remove("active");
    }
  }

  showGame() {
    this.showPanel(null);
    this.hud.classList.remove("hidden");
    this.touchControls.classList.add("active");
  }

  connection(online, detail) {
    const badge = document.querySelector("#connection-badge");
    badge.classList.toggle("online", online);
    badge.textContent = online ? "ONLINE" : detail === "retrying" ? "RECONNECTING…" : "OFFLINE — RECONNECTING";
  }

  lobby(data, localId) {
    this.showPanel("lobby");
    const players = data.players ?? [];
    document.querySelector("#lobby-title").textContent = players.length > 1 ? `${players.length} racers ready!` : "Finding racers…";
    document.querySelector("#player-list").innerHTML = players.map((player) => `
      <div class="player-card">
        <span class="player-dot" title="${escapeHtml(colorLabel(player.color))}" style="background:${safeColor(player.color)}"></span>
        <span>${escapeHtml(player.name)}</span>
        <em>${player.id === localId ? "YOU" : "READY"}</em>
      </div>
    `).join("");
    const countdown = data.countdown ?? data.startsIn;
    document.querySelector("#countdown-value").textContent = Number.isFinite(countdown) ? Math.max(1, Math.ceil(countdown / (countdown > 100 ? 1000 : 1))) : "…";
    const me = players.find((player) => player.id === localId);
    const taken = colorTakenMessage(me);
    document.querySelector("#lobby-hint").textContent = taken
      ?? (players.length < 2 ? "Solo start if nobody joins" : "The race will start soon");
  }

  raceCountdown(seconds, audio) {
    const number = Math.max(0, Math.ceil(seconds));
    if (number === this.lastCountdown) return;
    this.lastCountdown = number;
    const element = document.querySelector("#race-countdown");
    element.textContent = number === 0 ? "GO!" : String(number);
    element.classList.remove("pop");
    void element.offsetWidth;
    element.classList.add("pop");
    audio?.countdown(number);
  }

  updateHud({ place = 1, total = 1, checkpoint = 0, checkpointTotal = 4, waterDistance = 10, waterPaused = false }) {
    document.querySelector("#place-value").textContent = `${place} / ${total}`;
    document.querySelector("#checkpoint-value").textContent = `${checkpoint} / ${checkpointTotal}`;
    const waterPill = document.querySelector("#water-pill");
    waterPill.classList.toggle("close", !waterPaused && waterDistance < 4);
    document.querySelector("#water-value").textContent = waterPaused
      ? "TIDE PAUSED"
      : waterDistance <= 0
        ? "TOO CLOSE!"
        : `${Math.ceil(waterDistance)}m below`;
  }

  toast(message, duration = 1700) {
    clearTimeout(this.toastTimer);
    this.toastElement.textContent = message;
    this.toastElement.classList.add("show");
    this.toastTimer = setTimeout(() => this.toastElement.classList.remove("show"), duration);
  }

  results(data, localId) {
    this.showPanel("results");
    const players = [...(data.players ?? [])].sort((a, b) => (a.place ?? 99) - (b.place ?? 99));
    const me = players.find((player) => player.id === localId);
    const winner = me?.place === 1 && !me?.eliminated;
    document.querySelector("#result-title").textContent = winner ? "High and dry!" : me?.eliminated ? "The flood got you!" : "Race complete!";
    document.querySelector("#result-subtitle").textContent = winner
      ? "First to the rescue beacon."
      : "Every climb makes the next one easier.";
    document.querySelector("#result-list").innerHTML = players.map((player, index) => `
      <div class="result-row">
        <span class="rank">${player.place ?? index + 1}</span>
        <span class="player-dot" title="${escapeHtml(colorLabel(player.color))}" style="background:${safeColor(player.color)}"></span>
        <span>${escapeHtml(player.name)}${player.id === localId ? " (you)" : ""}</span>
        <span class="status">${player.eliminated ? "SPLASHED" : "SAFE"}</span>
      </div>
    `).join("");
  }
}

function escapeHtml(value = "") {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}

function safeColor(value) {
  return /^#[0-9a-f]{6}$/i.test(value ?? "") ? value : "#ff7b35";
}

function colorLabel(hex) {
  return PLAYER_COLORS.find((color) => color.hex.toLowerCase() === String(hex ?? "").toLowerCase())?.label ?? "Racer";
}

export function colorTakenMessage(player) {
  if (!player?.colorTaken) return null;
  const wanted = PLAYER_COLORS.find((color) => color.id === player.requestedColor);
  const assigned = PLAYER_COLORS.find((color) => color.hex.toLowerCase() === String(player.color).toLowerCase());
  if (!wanted || !assigned) return "That color was taken. This one is yours.";
  return `${wanted.label} is taken. You're ${assigned.label}.`;
}
